import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, LayoutTemplate, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { AiAgent, AiAgentTemplate } from "@/model/interface/AiAgent";
import TemplateBasicsSection from "../template-sections/TemplateBasicsSection";

export type WizardStartMode = "new" | "template" | "agent";

const START_MODES: { mode: WizardStartMode; title: string; description: string; icon: React.ElementType }[] = [
  {
    mode: "new",
    title: "A new kind of agent",
    description: "Build a template from scratch: prompt, skills, voice and conversation.",
    icon: Sparkles,
  },
  {
    mode: "template",
    title: "Another agent on a template",
    description: "Reuse a template you already built and give it to an organisation.",
    icon: LayoutTemplate,
  },
  {
    mode: "agent",
    title: "A copy of an existing agent",
    description: "Start from a deployed agent's configuration. No organisation data is copied.",
    icon: Copy,
  },
];

export default function WizardStartStep({
  startMode,
  onStartModeChange,
  existingTemplateId,
  onExistingTemplateChange,
  copyAgentId,
  onCopyAgentChange,
  templates,
  deployedAgents,
}: {
  startMode: WizardStartMode;
  onStartModeChange: (mode: WizardStartMode) => void;
  existingTemplateId: string;
  onExistingTemplateChange: (templateId: string) => void;
  copyAgentId: string;
  onCopyAgentChange: (agentId: string) => void;
  templates: AiAgentTemplate[];
  deployedAgents: AiAgent[];
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {START_MODES.map((option) => {
          const OptionIcon = option.icon;
          const isSelected = startMode === option.mode;
          return (
            <button
              key={option.mode}
              type="button"
              onClick={() => onStartModeChange(option.mode)}
              className={cn(
                "rounded-xl border p-4 text-left transition-colors",
                isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
              )}
            >
              <OptionIcon className={cn("mb-2 h-5 w-5", isSelected ? "text-primary" : "text-muted-foreground")} />
              <p className="text-sm font-semibold">{option.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
            </button>
          );
        })}
      </div>

      {startMode === "new" && <TemplateBasicsSection />}

      {startMode === "template" && (
        <Card>
          <CardHeader>
            <CardTitle>Pick a template</CardTitle>
            <CardDescription>The next step is deploying an agent built on it</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-md space-y-1.5">
              <Label>Template *</Label>
              <Select value={existingTemplateId} onValueChange={onExistingTemplateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a template" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.status && template.status !== "PUBLISHED" ? `(${template.status.toLowerCase()})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                You can still change the template's prompt, skills and voice from the steps above.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {startMode === "agent" && (
        <Card>
          <CardHeader>
            <CardTitle>Pick an agent to copy</CardTitle>
            <CardDescription>Its configuration is copied, never the organisation's data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-md space-y-1.5">
              <Label>Source agent *</Label>
              <Select value={copyAgentId} onValueChange={onCopyAgentChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a deployed agent" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {deployedAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} · {agent.organisation?.name ?? "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
